import { useState } from 'react';

interface CopyBookmarkletLinkButtonProps {
  href: string;
}

/**
 * A plain `navigator.clipboard.writeText` copy button for the bookmarklet
 * link — offered alongside the draggable BookmarkletLink as a more
 * predictable alternative to a mobile browser's long-press "copy link"
 * context menu on a styled anchor, which behaved inconsistently for a link
 * this long in at least one real case (see docs/automation-architecture.md).
 */
export default function CopyBookmarkletLinkButton({ href }: CopyBookmarkletLinkButtonProps) {
  const [state, setState] = useState<'idle' | 'copied' | 'error'>('idle');

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(href);
      setState('copied');
      setTimeout(() => setState('idle'), 2000);
    } catch {
      setState('error');
    }
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => void handleCopy()}
        className="rounded-lg bg-slate-100 px-4 py-2 text-sm font-medium text-slate-700 active:bg-slate-200"
      >
        {state === 'copied' ? 'Copied ✓' : '📋 Copy Bookmarklet Link'}
      </button>
      {state === 'error' && (
        <p role="alert" className="mt-1 text-xs text-red-700">
          Couldn't copy automatically — long-press the button above instead.
        </p>
      )}
    </div>
  );
}
